import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://tevv-pipeline-api.com'

// Helper function to construct WebSocket URL from API URL
export const getWebSocketUrl = (path) => {
  const wsBaseUrl = API_BASE_URL.replace('http://', 'ws://').replace('https://', 'wss://')
  const fullWsUrl = `${wsBaseUrl}${path}`
  console.log('🔗 WebSocket URL constructed:', fullWsUrl)
  return fullWsUrl
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('sessionToken') || localStorage.getItem('authToken')
    console.log('🔐 API Request:', config.method?.toUpperCase(), config.url, 'Has token:', !!token)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      console.log('🔑 Added Authorization header')
    } else {
      console.warn('⚠️ No token found for API request')
    }
    return config
  },
  (error) => {
    console.error('❌ Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.config.method?.toUpperCase(), response.config.url, 'Status:', response.status)
    return response
  },
  async (error) => {
    console.error('❌ API Error:', error.config?.method?.toUpperCase(), error.config?.url, 'Status:', error.response?.status, error.message)
    
    // Only clear auth and redirect for specific 401 scenarios
    if (error.response?.status === 401) {
      console.warn('⚠️ 401 Unauthorized response received')
      
      // Don't auto-logout for session endpoints - let the component handle it
      const isSessionEndpoint = error.config?.url?.includes('/sessions/') || 
                                error.config?.url?.includes('/answers/')
      
      if (!isSessionEndpoint) {
        console.error('🚪 Logging out due to 401 on non-session endpoint')
        localStorage.removeItem('sessionToken')
        localStorage.removeItem('authToken')
        localStorage.removeItem('user')
        
        // Only redirect if we're not already on the login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      } else {
        console.log('📁 401 on session endpoint - letting component handle it')
      }
    }
    
    return Promise.reject(error)
  }
)

// Auth endpoints
export const authAPI = {
  login: async (email, password) => {
    console.log('🌐 Calling login API with:', { email })
    const response = await api.post('/auth/login', { email, password })
    console.log('🌐 Login API response:', response.data)
    return response.data
  },
  
  logout: async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Even if logout fails on server, clear local data
      console.error('Logout error:', error)
    }
  },
  
  verify: async () => {
    const response = await api.get('/auth/verify')
    return response.data
  },
  
  refresh: async () => {
    const response = await api.post('/auth/refresh')
    return response.data
  }
}

// Search endpoints
export const searchAPI = {
  startSearch: async (query) => {
    const response = await api.post('/search', { query })
    return response.data
  },
  
  getAnswer: async (sessionId) => {
    const response = await api.get(`/answers/${sessionId}`)
    return response.data
  },
  
  getAnswers: async (page = 1, limit = 50) => {
    const response = await api.get(`/answers?page=${page}&limit=${limit}`)
    return response.data
  },
  
  getConversationMessages: async (sessionId) => {
    const response = await api.get(`/conversations/${sessionId}/messages`)
    return response.data
  },
  
  getConversations: async (page = 1, limit = 50) => {
    const response = await api.get(`/conversations?page=${page}&limit=${limit}`)
    return response.data
  },
  
  createConversation: async (title = null, initialPrompt = null) => {
    const response = await api.post('/conversations', { title, initial_prompt: initialPrompt })
    return response.data
  },
  
  addMessage: async (conversationId, content, senderRole = 'user', phase = 'user_prompt', metadata = null) => {
    const response = await api.post(`/conversations/${conversationId}/messages`, {
      content,
      sender_role: senderRole,
      phase,
      metadata
    })
    return response.data
  }
}

// Persona endpoints
export const personaAPI = {
  getDimensions: async () => {
    const response = await api.get('/personas/dimensions')
    return response.data
  },

  generate: async (formData) => {
    // Build request body with snake_case keys
    const body = {
      sleep_disorder_type: formData.sleepDisorderType,
      primary_cause: formData.primaryCause,
      treatment_approach: formData.treatmentApproach,
    }
    // Add optional extended fields
    if (formData.demographics) {
      body.demographics = {
        age_range: formData.demographics.ageRange || undefined,
        gender: formData.demographics.gender || undefined,
        occupation: formData.demographics.occupation || undefined,
        socioeconomic_status: formData.demographics.socioeconomicStatus || undefined,
      }
    }
    if (formData.clinicalAdditions) {
      body.clinical_additions = {
        comorbid_conditions: formData.clinicalAdditions.comorbidConditions?.length ? formData.clinicalAdditions.comorbidConditions : undefined,
        symptom_duration: formData.clinicalAdditions.symptomDuration || undefined,
        prior_treatments: formData.clinicalAdditions.priorTreatments?.length ? formData.clinicalAdditions.priorTreatments : undefined,
      }
    }
    if (formData.contextualFactors) {
      body.contextual_factors = {
        family_situation: formData.contextualFactors.familySituation || undefined,
        work_schedule: formData.contextualFactors.workSchedule || undefined,
        caregiving_responsibilities: formData.contextualFactors.caregivingResponsibilities || undefined,
        access_to_care: formData.contextualFactors.accessToCare || undefined,
        insurance_financial: formData.contextualFactors.insuranceFinancial || undefined,
        technology_comfort: formData.contextualFactors.technologyComfort || undefined,
        trust_in_clinicians: formData.contextualFactors.trustInClinicians || undefined,
      }
    }
    if (formData.archetype) body.archetype = formData.archetype
    if (formData.environmentalFactors) {
      body.environmental_factors = {
        adr_probability: formData.environmentalFactors.adrProbability ?? undefined,
        treatment_complication_probability: formData.environmentalFactors.treatmentComplicationProbability ?? undefined,
        life_events: formData.environmentalFactors.lifeEvents || undefined,
        system_barriers: formData.environmentalFactors.systemBarriers || undefined,
      }
    }
    if (formData.agentChoices) {
      body.agent_choices = {
        medication_adherence_probability: formData.agentChoices.medicationAdherenceProbability ?? undefined,
        therapy_compliance_probability: formData.agentChoices.therapyComplianceProbability ?? undefined,
        lifestyle_change_probability: formData.agentChoices.lifestyleChangeProbability ?? undefined,
      }
    }
    const response = await api.post('/personas/generate', body)
    return response.data
  },

  list: async () => {
    const response = await api.get('/personas')
    return response.data
  },

  get: async (personaId) => {
    const response = await api.get(`/personas/${personaId}`)
    return response.data
  },

  delete: async (personaId) => {
    const response = await api.delete(`/personas/${personaId}`)
    return response.data
  },

  exportJson: (personaId) => {
    const token = localStorage.getItem('sessionToken') || localStorage.getItem('authToken')
    const url = `${API_BASE_URL}/personas/${personaId}/export`
    // Trigger download via hidden link
    const link = document.createElement('a')
    link.href = url
    // Use fetch to add auth header for download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob)
        link.href = blobUrl
        link.download = `persona_${personaId}.json`
        link.click()
        URL.revokeObjectURL(blobUrl)
      })
  }
}

// Journey endpoints
export const journeyAPI = {
  generate: async (personaId, numEpisodes) => {
    const response = await api.post('/journeys/generate', {
      persona_id: personaId,
      num_episodes: numEpisodes
    })
    return response.data
  },

  list: async () => {
    const response = await api.get('/journeys')
    return response.data
  },

  get: async (journeyId) => {
    const response = await api.get(`/journeys/${journeyId}`)
    return response.data
  },

  exportJson: (journeyId) => {
    const token = localStorage.getItem('sessionToken') || localStorage.getItem('authToken')
    const url = `${API_BASE_URL}/journeys/${journeyId}/export`
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `journey_${journeyId}.json`
        link.click()
        URL.revokeObjectURL(blobUrl)
      })
  }
}

// Evaluation endpoints
export const evaluationAPI = {
  evaluateJourney: async (journeyId) => {
    const response = await api.post(`/evaluations/journey/${journeyId}`)
    return response.data
  },

  evaluateBatch: async (journeyIds) => {
    const response = await api.post('/evaluations/batch/triage', { journey_ids: journeyIds })
    return response.data
  },

  getResults: async (journeyId) => {
    const response = await api.get(`/evaluations/journey/${journeyId}/results`)
    return response.data
  },

  getConfig: async () => {
    const response = await api.get('/evaluations/config')
    return response.data
  },

  // LLM-Judge methods
  llmJudgeEvaluate: async (journeyId, sampleEpisodes = false) => {
    const response = await api.post(`/evaluations/llm-judge/journey/${journeyId}`, {
      sample_episodes: sampleEpisodes
    })
    return response.data
  },

  getLLMJudgeResults: async (journeyId) => {
    const response = await api.get(`/evaluations/llm-judge/journey/${journeyId}/results`)
    return response.data
  },

  getRubric: async () => {
    const response = await api.get('/evaluations/llm-judge/rubric')
    return response.data
  },

  updateRubric: async (rubric) => {
    const response = await api.put('/evaluations/llm-judge/rubric', rubric)
    return response.data
  },

  // Multi-model judge methods
  multiJudgeEvaluate: async (journeyId, providers = ['openai', 'anthropic', 'gemini'], sampleEpisodes = false) => {
    const response = await api.post(`/evaluations/multi-judge/journey/${journeyId}`, {
      providers,
      sample_episodes: sampleEpisodes
    }, { timeout: 600000 }) // 10 min timeout for multi-model reasoning models
    return response.data
  },

  getMultiJudgeComparison: async (journeyId) => {
    const response = await api.get(`/evaluations/multi-judge/journey/${journeyId}/comparison`)
    return response.data
  },

  getAvailableProviders: async () => {
    const response = await api.get('/evaluations/multi-judge/providers')
    return response.data
  },

  exportResults: async (journeyIds, format = 'json', allCompleted = false) => {
    const response = await api.post('/evaluations/export', {
      journey_ids: journeyIds,
      format,
      all_completed: allCompleted
    }, { responseType: 'blob' })
    const blob = new Blob([response.data])
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `evaluation_results.${format}`
    link.click()
    URL.revokeObjectURL(url)
  }
}

// Batch job endpoints
export const batchAPI = {
  startGeneration: async (config) => {
    const response = await api.post('/batch/generation/start', config)
    return response.data
  },

  startEvaluation: async (config) => {
    const response = await api.post('/batch/evaluation/start', config)
    return response.data
  },

  getJob: async (jobId) => {
    const response = await api.get(`/batch/jobs/${jobId}`)
    return response.data
  },

  listJobs: async (jobType = null) => {
    const params = jobType ? `?job_type=${jobType}` : ''
    const response = await api.get(`/batch/jobs${params}`)
    return response.data
  },

  cancelJob: async (jobId) => {
    const response = await api.post(`/batch/jobs/${jobId}/cancel`)
    return response.data
  },

  getJobJourneys: async (jobId) => {
    const response = await api.get(`/batch/jobs/${jobId}/journeys`)
    return response.data
  }
}

// Journey export extension
journeyAPI.exportBatch = async (journeyIds, format = 'json', allCompleted = false) => {
  const response = await api.post('/journeys/export-batch', {
    journey_ids: journeyIds,
    format,
    all_completed: allCompleted
  }, { responseType: 'blob' })
  const blob = new Blob([response.data])
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `journeys_export.${format}`
  link.click()
  URL.revokeObjectURL(url)
}

// OSA multi-agent pipeline endpoints
export const osaAPI = {
  listCases: async () => {
    const response = await api.get('/osa/cases')
    return response.data
  },
  run: async ({ caseId, providers = ['openai'], skipEvaluation = false, behaviorConfig = null }) => {
    const response = await api.post('/osa/run', {
      case_id: caseId,
      providers,
      skip_evaluation: skipEvaluation,
      behavior_config: behaviorConfig,
    }, { timeout: 600000 })
    return response.data
  },
  populationRun: async ({ caseId, personaTag = null, nSamples = 5, providers = ['openai'] }) => {
    const response = await api.post('/osa/population-run', {
      case_id: caseId,
      persona_tag: personaTag,
      n_samples: nSamples,
      providers,
    }, { timeout: 600000 })
    return response.data
  },
  getPersonaPriors: async () => {
    const response = await api.get('/osa/persona-priors')
    return response.data
  },
}

export default api